// The label dashboard's release cards (ART-335): each artist's soonest upcoming release, else their most recent.
// Two scopes over one server read — All roster (org-scoped, keyed ['org', …]) and the artist page (band-scoped,
// keyed ['band', bandId, …] so an artist switch scorches it; the band-direct path is spliced under the roster's
// org lens by the middleware). Money-free except the nullable `budget` block (marketing money only). Not enveloped.
import { useQuery } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type RosterReleaseCardDto = components["schemas"]["RosterReleaseCard"];
export type ReleaseCardStep = components["schemas"]["ReleaseCardStep"];
export type RosterLastRelease = components["schemas"]["RosterLastRelease"];

/** The card with its always-present fields made required (the generated shape marks every field optional). */
export type RosterReleaseCard = RosterReleaseCardDto & {
  bandId: string;
  artistName: string;
  releaseId: string;
  title: string;
  assetsAdded: number;
  assetsInProgress: number;
  assetsTotal: number;
  openTodos: number;
  overdueTodos: number;
  smartLinkVisits30d: number;
  timeline: ReleaseCardStep[];
};

function toCard(c: RosterReleaseCardDto): RosterReleaseCard {
  return {
    ...c,
    bandId: c.bandId ?? "",
    artistName: c.artistName ?? "",
    releaseId: c.releaseId ?? "",
    title: c.title ?? "",
    assetsAdded: c.assetsAdded ?? 0,
    assetsInProgress: c.assetsInProgress ?? 0,
    assetsTotal: c.assetsTotal ?? 0,
    openTodos: c.openTodos ?? 0,
    overdueTodos: c.overdueTodos ?? 0,
    smartLinkVisits30d: c.smartLinkVisits30d ?? 0,
    timeline: c.timeline ?? [],
  };
}

export function useRosterReleaseCards(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "roster", "release-cards"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/release-cards", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return (data.items ?? []).map(toCard);
    },
    staleTime: 60_000,
  });
}

export function useArtistReleaseCard() {
  const { api } = useApp();
  return useBandQuery<{ card: RosterReleaseCard | null; lastRelease: RosterLastRelease | null }>(
    ["release-card"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/release-card", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return { card: data.card ? toCard(data.card) : null, lastRelease: data.lastRelease ?? null };
    },
    { staleTime: 60_000 },
  );
}
